import React, { useEffect, useState, useCallback, Suspense } from 'react';
import { motion } from 'framer-motion';
import { Radio, Globe2, CheckCircle2, ShieldCheck, Gauge } from 'lucide-react';
import { useLang } from '@/context/LanguageContext';
import { ScrollReveal } from '@/components/common/Primitives';
import { useLiveChannel } from '@/lib/live';
import api from '@/lib/api';

const EnterpriseGlobe = React.lazy(() => import('@/components/wow/EnterpriseGlobe'));

const STATUS_COLOR = { operational: '#2EF2A5', degraded: '#FFCC66', maintenance: '#5AF3FF', outage: '#FF5C7A' };
const STATUS_LABEL = { operational: 'Betriebsbereit', degraded: 'Eingeschränkt', maintenance: 'Wartung', outage: 'Störung' };

/**
 * PRIVACY-SAFE global availability section for the PUBLIC homepage.
 * Shows only generic service status (from /api/status) + aggregate metrics.
 * No hostnames, IPs, node/VM/storage names or any infrastructure detail.
 */
export default function GlobalThreatSection() {
  const { t } = useLang();
  const [status, setStatus] = useState(null);
  const [globe, setGlobe] = useState({ sites: [], arcs: [] });
  const [pulses, setPulses] = useState([]);

  useEffect(() => {
    let alive = true;
    const load = () => api.get('/status').then((r) => { if (alive) setStatus(r.data); }).catch(() => {});
    load();
    const i = setInterval(load, 20000);
    return () => { alive = false; clearInterval(i); };
  }, []);

  useEffect(() => {
    let alive = true;
    api.get('/globe').then((r) => { if (alive && r.data?.data) setGlobe(r.data.data); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const onPulse = useCallback((msg) => {
    if (!msg || !msg.data) return;
    setPulses((p) => [msg.data, ...p].slice(0, 16));
  }, []);
  useLiveChannel('globe', onPulse);

  const overall = status?.overall || 'operational';
  const services = status?.services || [];

  return (
    <section id="noc" className="relative z-10 py-20 lg:py-28" data-testid="global-availability-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal>
          <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-[color:var(--itw-cyan)]">
            <Radio className="h-3.5 w-3.5 animate-pulse" /> Global Service Availability
          </div>
          <div className="mt-3 max-w-2xl">
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[color:var(--itw-text)]">Weltweit verfügbar, rund um die Uhr überwacht</h2>
            <p className="mt-3 text-[color:var(--itw-text-2)]">Unsere Dienste laufen auf redundanter, global verteilter Infrastruktur. Der Live-Status wird transparent bereitgestellt – technische Details bleiben sicher intern.</p>
          </div>
        </ScrollReveal>

        <div className="mt-10 grid lg:grid-cols-2 gap-8 items-stretch">
          {/* GLOBE (decorative, no secrets) */}
          <ScrollReveal>
            <div className="relative glass rounded-2xl border border-[color:var(--itw-border-strong)] overflow-hidden h-[420px] sm:h-[480px]">
              <div className="absolute top-0 inset-x-0 z-10 flex items-center justify-between px-4 py-3 border-b border-[color:var(--itw-border)] bg-[color:var(--itw-surface)]/40 backdrop-blur-md">
                <span className="flex items-center gap-2 font-mono text-[11px] text-[color:var(--itw-text-2)]"><Globe2 className="h-3.5 w-3.5 text-[color:var(--itw-cyan)]" /> global-network</span>
                <span className="flex items-center gap-1.5 font-mono text-[11px]" style={{ color: STATUS_COLOR[overall] }}>
                  <span className="h-1.5 w-1.5 rounded-full animate-blink" style={{ background: STATUS_COLOR[overall] }} /> {STATUS_LABEL[overall]}
                </span>
              </div>
              <Suspense fallback={<div className="absolute inset-0 grid place-items-center text-[color:var(--itw-text-3)] font-mono text-sm">initializing globe…</div>}>
                <EnterpriseGlobe sites={globe.sites} arcs={globe.arcs} pulses={pulses} />
              </Suspense>
              <div className="absolute bottom-0 inset-x-0 z-10 flex items-center justify-between px-4 py-2.5 border-t border-[color:var(--itw-border)] bg-[color:var(--itw-surface)]/40 backdrop-blur-md font-mono text-[11px] text-[color:var(--itw-text-3)]">
                <span>Verfügbarkeit: <span className="text-[color:var(--itw-cyan)]">99.98%</span></span>
                <span>Regionen: <span className="text-[color:var(--itw-cyan-soft)]">EU · US · APAC</span></span>
              </div>
            </div>
          </ScrollReveal>

          {/* GENERIC STATUS PANEL */}
          <ScrollReveal delay={0.1}>
            <div className="glass rounded-2xl border border-[color:var(--itw-border)] p-5 h-full flex flex-col" data-testid="global-availability-panel">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl bg-[color:var(--itw-surface-2)] border border-[color:var(--itw-border)] p-4">
                  <div className="flex items-center justify-between"><span className="text-[11px] uppercase tracking-wider text-[color:var(--itw-text-3)] font-mono">Uptime</span><Gauge className="h-3.5 w-3.5 text-[color:var(--itw-cyan)]" /></div>
                  <div className="mt-1.5 font-mono text-2xl font-semibold text-[color:var(--itw-text)]">99.98%</div>
                </div>
                <div className="rounded-xl bg-[color:var(--itw-surface-2)] border border-[color:var(--itw-border)] p-4">
                  <div className="flex items-center justify-between"><span className="text-[11px] uppercase tracking-wider text-[color:var(--itw-text-3)] font-mono">Dienste</span><CheckCircle2 className="h-3.5 w-3.5 text-[#2EF2A5]" /></div>
                  <div className="mt-1.5 font-mono text-2xl font-semibold text-[color:var(--itw-text)]">{services.length || '—'}</div>
                </div>
                <div className="rounded-xl bg-[color:var(--itw-surface-2)] border border-[color:var(--itw-border)] p-4">
                  <div className="flex items-center justify-between"><span className="text-[11px] uppercase tracking-wider text-[color:var(--itw-text-3)] font-mono">Schutz</span><ShieldCheck className="h-3.5 w-3.5 text-[color:var(--itw-cyan)]" /></div>
                  <div className="mt-1.5 font-mono text-2xl font-semibold text-[color:var(--itw-text)]">24/7</div>
                </div>
              </div>

              <div className="mt-4 flex-1 flex flex-col min-h-0">
                <div className="text-[11px] uppercase tracking-wider text-[color:var(--itw-text-3)] font-mono mb-2">Service-Status</div>
                <div className="space-y-1.5 overflow-hidden">
                  {services.length === 0 && <div className="text-sm text-[color:var(--itw-text-3)] font-mono">lade…</div>}
                  {services.map((s) => (
                    <div key={s.name} className="flex items-center justify-between rounded-lg border border-[color:var(--itw-border)] px-3 py-2.5">
                      <span className="text-sm text-[color:var(--itw-text)]">{s.name}</span>
                      <span className="inline-flex items-center gap-1.5 text-[12px] font-medium" style={{ color: STATUS_COLOR[s.status] || '#2EF2A5' }}>
                        <span className="h-1.5 w-1.5 rounded-full" style={{ background: STATUS_COLOR[s.status] || '#2EF2A5' }} />{STATUS_LABEL[s.status] || s.status}
                      </span>
                    </div>
                  ))}
                </div>
                <a href="/status" className="mt-4 inline-flex items-center gap-1.5 text-sm text-[color:var(--itw-cyan)] hover:underline self-start" data-testid="global-status-link">Vollständige Statusseite →</a>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}
